import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Merge } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "Splitting Data",
        icon: "✂️",
        content: (
            <div className="space-y-6">
                <div className="bg-amber-50 p-6 rounded-2xl border-2 border-amber-200">
                    <h3 className="font-bold text-xl text-amber-800 mb-3">Don't Repeat Yourself</h3>
                    <p className="text-slate-700 mb-4">
                        In a database, we don't put everything in one giant list. We split it up!
                    </p>
                    <p className="text-slate-700">
                        Example: One list for <strong>Employees</strong>, one list for <strong>Departments</strong>.
                        We link them with IDs.
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "The Matchmaker",
        icon: "🔗",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600">A <strong>JOIN</strong> connects two tables back together based on a matching column.</p>
                <div className="grid grid-cols-2 gap-4 text-xs font-mono">
                    <div className="bg-white border rounded p-2">
                        <div className="font-bold border-b mb-1">Users</div>
                        <div>ID: 1 | Name: Alice | Dept: 7</div>
                    </div>
                    <div className="bg-white border rounded p-2">
                        <div className="font-bold border-b mb-1">Depts</div>
                        <div>ID: 7 | Name: Sales</div>
                    </div>
                </div>
                <div className="bg-green-100 p-2 rounded text-center text-green-800 font-bold text-sm">
                    Result: Alice works in Sales
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Mission Brief",
        icon: "🧩",
        content: (
            <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-200">
                    <h3 className="font-bold text-xl text-blue-800 mb-3">Venn Diagrams</h3>
                    <p className="text-slate-700 mb-4">
                        Use the visualizer to explore different join types.
                    </p>
                    <p className="font-bold text-blue-900">
                        Click all 4 types (Inner, Left, Right, Full) to master this module!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME LOGIC ============
const TABLE_A = [
    { id: 1, name: 'Alice', dept_id: 1 },
    { id: 2, name: 'Bob', dept_id: 2 },
    { id: 3, name: 'Charlie', dept_id: null },
];

const TABLE_B = [
    { id: 1, name: 'Engineering' },
    { id: 2, name: 'Marketing' },
    { id: 3, name: 'Sales' },
];

type JoinType = 'inner' | 'left' | 'right' | 'full';

const JoinGame = ({ onComplete }: { onComplete: () => void }) => {
    const [joinType, setJoinType] = useState<JoinType>('inner');
    const [testedJoins, setTestedJoins] = useState<Set<JoinType>>(new Set(['inner']));

    const handleJoinChange = (type: JoinType) => {
        setJoinType(type);
        const newTested = new Set(testedJoins).add(type);
        setTestedJoins(newTested);

        if (newTested.size >= 4) {
            onComplete();
        }
    };

    const getJoinResult = () => {
        switch (joinType) {
            case 'inner':
                return TABLE_A.filter(a => a.dept_id !== null).map(a => ({
                    ...a,
                    department: TABLE_B.find(b => b.id === a.dept_id)?.name
                }));
            case 'left':
                return TABLE_A.map(a => ({
                    ...a,
                    department: TABLE_B.find(b => b.id === a.dept_id)?.name || null
                }));
            case 'right':
                return TABLE_B.map(b => {
                    const employee = TABLE_A.find(a => a.dept_id === b.id);
                    return {
                        id: employee?.id || null,
                        name: employee?.name || null,
                        dept_id: b.id,
                        department: b.name
                    };
                });
            case 'full':
                const leftResults = TABLE_A.map(a => ({
                    id: a.id,
                    name: a.name,
                    dept_id: a.dept_id,
                    department: TABLE_B.find(b => b.id === a.dept_id)?.name || null
                }));
                const rightOnly = TABLE_B.filter(b => !TABLE_A.some(a => a.dept_id === b.id)).map(b => ({
                    id: null,
                    name: null,
                    dept_id: b.id,
                    department: b.name
                }));
                return [...leftResults, ...rightOnly];
            default:
                return [];
        }
    };

    const joinResult = getJoinResult();

    return (
        <div className="flex flex-col items-center">
            {/* Join Type Buttons */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
                {(['inner', 'left', 'right', 'full'] as JoinType[]).map(type => (
                    <button
                        key={type}
                        onClick={() => handleJoinChange(type)}
                        className={`px-6 py-3 rounded-xl text-sm font-bold border-2 uppercase tracking-wide transition-all ${joinType === type
                            ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300'
                            } ${testedJoins.has(type) ? 'ring-2 ring-green-400 ring-offset-2' : ''}`}
                    >
                        {type} Join {testedJoins.has(type) && '✅'}
                    </button>
                ))}
            </div>

            <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Venn Diagram */}
                <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-slate-100 flex items-center justify-center">
                    <svg viewBox="0 0 300 200" className="w-full max-w-md">
                        {/* Left Circle (Employees) */}
                        <motion.circle
                            cx="110"
                            cy="100"
                            r="70"
                            fill={joinType === 'left' || joinType === 'full' ? '#fcd34d' : 'transparent'}
                            stroke="#f59e0b"
                            strokeWidth="3"
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                        />
                        {/* Right Circle (Departments) */}
                        <motion.circle
                            cx="190"
                            cy="100"
                            r="70"
                            fill={joinType === 'right' || joinType === 'full' ? '#93c5fd' : 'transparent'}
                            stroke="#3b82f6"
                            strokeWidth="3"
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                        />
                        {/* Intersection */}
                        <motion.path
                            d="M150,45 A70,70 0 0,1 150,155 A70,70 0 0,1 150,45"
                            fill={joinType === 'inner' ? '#34d399' : joinType === 'left' || joinType === 'right' ? '#6ee7b7' : '#34d399'}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        />
                        {/* Labels */}
                        <text x="70" y="100" textAnchor="middle" className="text-sm font-bold fill-amber-800">Employees</text>
                        <text x="230" y="100" textAnchor="middle" className="text-sm font-bold fill-blue-800">Depts</text>
                        <text x="150" y="105" textAnchor="middle" className="text-xs font-bold fill-green-800">Match</text>
                    </svg>
                </div>

                {/* Tables */}
                <div className="space-y-6">
                    {/* Source Tables */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-amber-50 rounded-xl p-4 border-2 border-amber-200">
                            <h3 className="font-bold text-amber-800 mb-2 text-sm">employees</h3>
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-amber-600">
                                        <th className="text-left p-1">id</th>
                                        <th className="text-left p-1">name</th>
                                        <th className="text-left p-1">dept_id</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {TABLE_A.map(row => (
                                        <tr key={row.id} className="text-amber-900">
                                            <td className="p-1">{row.id}</td>
                                            <td className="p-1">{row.name}</td>
                                            <td className="p-1">{row.dept_id ?? 'NULL'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                            <h3 className="font-bold text-blue-800 mb-2 text-sm">departments</h3>
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-blue-600">
                                        <th className="text-left p-1">id</th>
                                        <th className="text-left p-1">name</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {TABLE_B.map(row => (
                                        <tr key={row.id} className="text-blue-900">
                                            <td className="p-1">{row.id}</td>
                                            <td className="p-1">{row.name}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Result Table */}
                    <div className="bg-green-50 rounded-xl p-4 border-2 border-green-200 min-h-[160px]">
                        <h3 className="font-bold text-green-800 mb-2 flex items-center gap-2">
                            Result: <span className="uppercase">{joinType} JOIN</span>
                        </h3>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-green-600 border-b border-green-200">
                                    <th className="text-left p-2">id</th>
                                    <th className="text-left p-2">name</th>
                                    <th className="text-left p-2">department</th>
                                </tr>
                            </thead>
                            <tbody>
                                {joinResult.map((row, i) => (
                                    <motion.tr
                                        key={i}
                                        className="text-green-900 border-b border-green-100"
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.05 }}
                                    >
                                        <td className="p-2">{row.id ?? <span className="text-slate-400">NULL</span>}</td>
                                        <td className="p-2">{row.name ?? <span className="text-slate-400">NULL</span>}</td>
                                        <td className="p-2">{row.department ?? <span className="text-slate-400">NULL</span>}</td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* SQL Query */}
            <div className="mt-8 bg-slate-900 rounded-2xl p-6 text-green-400 font-mono text-sm w-full max-w-2xl">
                <div className="text-slate-500 mb-2">-- Generated SQL</div>
                <div>SELECT e.id, e.name, d.name AS department</div>
                <div>FROM employees e</div>
                <div className="text-yellow-400">{joinType.toUpperCase()} JOIN</div>
                <div>departments d ON e.dept_id = d.id;</div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const SqlJoins = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('sql-joins', 100);
    };

    return (
        <LessonWrapper
            moduleName="SQL Joins"
            moduleEmoji="🔗"
            moduleColor="amber"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Visualizer"
        >
            <JoinGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default SqlJoins;
